import { Download, FolderInput, Tags, Trash2, X } from 'lucide-react';
import { Button } from '@/shared/components';
import { formatBytes } from '@/shared/lib/format';
import type { BulkAvailability } from './akteBulkRules';

/** Auslöser der Sammelaktionen plus das, was die Auswahl überhaupt zulässt. */
export interface BulkBarActions extends BulkAvailability {
  onMove: () => void;
  onReclassify: () => void;
  onDelete: () => void;
  onDownload: () => void;
}

interface Props {
  /** Anzahl aktuell ausgewählter Dateien. */
  count: number;
  /** Anzahl auswählbarer Dateien in der aktuellen Ansicht (für „alle" / indeterminate). */
  totalInView: number;
  onToggleAll: () => void;
  onClear: () => void;
  bulk: BulkBarActions;
}

const LOCKED_HINT = 'Alle ausgewählten Belege sind gesperrt (GoBD).';
const MIXED_HINT = 'Nur innerhalb einer Kategorie verschiebbar.';

/** Sammel-Toolbar über der Content-Liste, sobald mindestens eine Datei ausgewählt ist. */
export function AkteSelectionBar({ count, totalInView, onToggleAll, onClear, bulk }: Props) {
  const allSelected = count > 0 && count === totalInView;
  const someSelected = count > 0 && count < totalInView;
  const moveTitle = !bulk.canMove ? (bulk.canMutate ? MIXED_HINT : LOCKED_HINT) : undefined;
  const mutateTitle = !bulk.canMutate ? LOCKED_HINT : undefined;

  return (
    <div className="border-line bg-card-alt mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border px-3.5 py-2">
      <label className="flex min-h-11 shrink-0 cursor-pointer items-center gap-3">
        <input
          type="checkbox"
          checked={allSelected}
          ref={(el) => {
            if (el) el.indeterminate = someSelected;
          }}
          onChange={onToggleAll}
          aria-label={allSelected ? 'Auswahl aufheben' : 'Alle in diesem Ordner auswählen'}
          className="accent-sealwax h-4 w-4 cursor-pointer"
        />
        <span className="text-ink text-sm font-medium">
          {count} ausgewählt
          <span className="text-ink-faint font-normal"> · {formatBytes(bulk.totalBytes)}</span>
          {bulk.lockedCount > 0 && (
            <span className="text-ink-faint font-normal"> · {bulk.lockedCount} gesperrt</span>
          )}
        </span>
      </label>

      <div className="bg-line hidden h-5 w-px sm:block" aria-hidden />

      <div className="flex flex-wrap items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<FolderInput size={14} />}
          disabled={!bulk.canMove}
          title={moveTitle}
          onClick={bulk.onMove}
        >
          Verschieben
        </Button>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<Tags size={14} />}
          disabled={!bulk.canMutate}
          title={mutateTitle}
          onClick={bulk.onReclassify}
        >
          Kategorie ändern
        </Button>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<Download size={14} />}
          onClick={bulk.onDownload}
        >
          Herunterladen
        </Button>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<Trash2 size={14} />}
          disabled={!bulk.canMutate}
          title={mutateTitle}
          onClick={bulk.onDelete}
          className="text-sealwax hover:bg-sealwax/10"
        >
          Löschen
        </Button>
      </div>

      <button
        type="button"
        onClick={onClear}
        className="text-ink-faint hover:text-ink ml-auto inline-flex min-h-11 items-center gap-1 text-sm"
      >
        <X size={14} /> Auswahl aufheben
      </button>
    </div>
  );
}
